import { useState, useEffect, useRef } from "react";
import styled from "styled-components/macro";
import Modal from "react-modal";
import InlineSVG from "react-inlinesvg";
import { useSudowriteStore } from "../../../stores/sudowriteStore";
import { useProjectStore } from "../../../stores/projectStore";
import { themeVariables } from "../../../themeVariables";
import { useScenesMigrationStore } from "../../../stores/scenesMigrationStore";
import Project from "../../../models/Project";
import PrimaryButton from "../../PrimaryButton";
import usePrevious from "../../../hooks/usePrevious";
import { useToast } from "../../ToastWrapper";

const modalOuterStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    zIndex: "30000",
    width: "80vw",
    // height: "80vh",
    maxWidth: "880px",
    // maxHeight: "80vh",
    transform: "translate(-50%, -50%)",
    border: "none",
    borderRadius: "20px",
    boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.10), 0px 0px 2px 0px rgba(0, 0, 0, 0.25)",
    padding: "18px",
    overflow: "hidden",
    color: themeVariables.grey0,
    background: themeVariables.menuColorFullscreen,
    fontFamily: "Inter",
    pointerEvents: "auto",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
  },
};

const ModalInnerContainer = styled.div`
  background: ${themeVariables.menuColorFullscreen};
  display: flex;
  flex-direction: column;
  height: 100%;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${themeVariables.grey2};
    border-radius: 5px;
  }
`;

const ModalHeaderWaiting = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  text-align: left;
  justify-content: space-between;
  width: 100%;
  font-size: 20px;

  .modal_title {
    font-size: 20px;
    line-height: 1.3;
  }
  .close {
    text-align: right;
    cursor: pointer;

    svg {
      g {
        opacity: 1;
      }
      path {
        fill: ${themeVariables.grey0};
      }
    }
  }

  button {
    cursor: pointer !important;
    font-family: "Inter" !important;
  }

  .primary {
    background: ${themeVariables.primaryCTA};
    color: ${themeVariables.ctaTextColor};
    border: 0;
    border-radius: 8px;
    height: 40px;
    padding: 8px 16px;
    font-size: 16px;
    font-weight: 500;

    &:hover {
      background: ${themeVariables.primaryCATHover};
    }
  }

  .secondary {
    background: transparent;
    color: ${themeVariables.primaryCTAHighContrast};
    border: 0;
    border-radius: 8px;
    height: 40px;
    font-size: 16px;
  }
`;

const HeaderRightButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  width: fit-content;
  gap: 10px;
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  justify-content: space-between;
  width: 100%;

  .modal_title {
    font-size: 18px;
    font-weight: 500;
  }
  p {
    font-size: 16px;
    font-weight: 400;

    svg {
      height: 20px;
      width: 20px;
      margin-right: 3px;
      margin-left: 3px;
      position: relative;
      top: 3px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      align-self: center;

      path {
        fill: ${themeVariables.grey0};
      }
    }
  }
`;

const ModalContent = styled.div`
  line-height: 1.5em;
  margin-top: 20px;
  text-align: left;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 80vh;
  button {
    border: 0px;
    font-size: 16px;
    font-family: "Inter";
    text-decoration: underline;
    padding: 0;
    background: transparent;
    cursor: pointer;
    color: ${themeVariables.grey0};
  }

  p {
    text-align: left;
    margin-top: 20px;
    margin-bottom: 10px;
  }

  .image-wrapper {
    shape-outside: margin-box;
    float: right;
    margin-left: 20px;
    margin-bottom: 20px;
  }

  img {
    display: block;
    border-radius: 8px;
  }

  .line-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    text-align: left;
    gap: 10px;
    margin-bottom: 6px;
    svg {
      &.converted {
        path {
          fill: #3cc210;
        }
      }

      path {
        fill: ${themeVariables.grey0};
      }
    }
  }

  .faq-container {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    text-align: left;
    gap: 10px;
  }
  .faq-identifier {
    font-size: 16px;
    font-weight: 500;
  }
  .faq-content {
    &.faq-content-with-svg {
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;
      align-items: center;
      text-align: left;
    }
    svg {
      display: inline;
      height: 16px;
      width: 16px;
      margin-right: 3px;
      margin-left: 3px;
      path {
        fill: ${themeVariables.grey0};
      }
    }
    ul {
      list-style: none;
      padding-left: 0px;
    }
    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
    }
    li::before {
      content: "•";
      position: absolute;
      left: 0;
      color: currentColor;
    }
  }

  .spacer {
    height: 10px;
  }
`;

const ModalFooter = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  gap: 10px;
`;

function ProjectScenesUpdate() {
  const [isUpdating, setIsUpdating] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [faqExpanded, setFaqExpanded] = useState(false);
  const { setToast } = useToast();
  const subscription = useSudowriteStore((state) => state.subscription);
  const project = useProjectStore((state) => state.project);
  const isOpen = useScenesMigrationStore((state) => state.byProject[project.id]?.modalOpen);
  const setIsOpen = useScenesMigrationStore((state) => state.setModalOpen);
  const isActive = useScenesMigrationStore((state) => state.byProject[project.id]?.isActive);
  const steps = useScenesMigrationStore((state) => state.byProject[project.id]?.steps || []);
  const prevLocked = usePrevious(project.locked);
  const latestStepRef = useRef(null);

  // We only want to show it for the first project that they see.
  // After that, it can be triggered by a separate CTA in the prose generator
  // it should also open as soon as the project is locked for migration.
  useEffect(() => {
    if (project.locked === "scenesMigration") {
      setIsOpen(project.id, true);
      setIsUpdating(true);
      return;
    }
    if (!project.locked && prevLocked === "scenesMigration") {
      setCompleted(true);
      return;
    }
    if (
      //
      !subscription.hasSeenProjectScenesModal &&
      !project.useScenes
    ) {
      setIsOpen(project.id, true);
      return;
    }
  }, [project, project.locked, subscription, isActive, prevLocked, setIsOpen]);

  useEffect(() => {
    if (latestStepRef.current) {
      latestStepRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [steps]);


  useEffect(() => {
    const handleUpdate = async () => {
      try {
        setIsUpdating(true);
        await Project.convertToScenes(project.id, true);
        setIsOpen(project.id, true);
      } catch (e) {
        setToast("Error converting project to scenes.  Please wait a few minutes and try again.");
      }
    };
    if (!project.useScenes) {
      handleUpdate();
    }
  }, [project, project.id, setToast, setIsOpen]);

  const handleCloseFromUpdating = () => {
    if (completed) {
      setIsOpen(project.id, false);
      window.location.reload();
    }
  };

  const running = isActive || isUpdating || completed;

  return (
    <Modal
      isOpen={isOpen}
      style={modalOuterStyles}
    >
      <ModalInnerContainer>
        <ModalHeader>
          <div className="modal_title">Converting Beats to Scenes</div>
          <p>
            Don't worry, you can find your old Beats in the
            <InlineSVG
              height={16}
              width={16}
              src="/assets/clock.svg"
              alt="History"
              aria-hidden="true"
            />
            History for Scenes.
          </p>
        </ModalHeader>
        <ModalContent>
          {steps.map((step, index) => {
            const isSkip = step.startsWith("Skip");
            return (
              <div
                key={`step-${String(index)}`}
                ref={index === steps.length - 1 ? latestStepRef : null}
                className="line-item"
              >
                <InlineSVG
                  src={isSkip ? "/assets/skipped.svg" : "/assets/check-simple.svg"}
                  alt="Check"
                  aria-hidden="true"
                  className={isSkip ? "skipped" : "converted"}
                />
                <span dangerouslySetInnerHTML={{ __html: step.replace(/"([^"]+)"/, "<strong>$1</strong>") }} />
              </div>
            );
          })}
        </ModalContent>
        <ModalFooter>
          <PrimaryButton
            onClick={handleCloseFromUpdating}
            isLoading={isUpdating && !completed}
          >
            {!completed && "Updating..."}
            {completed && (
              <>
                <InlineSVG
                  src="/assets/party.svg"
                  alt="Check"
                  aria-hidden="true"
                />
                Done
              </>
            )}
          </PrimaryButton>
        </ModalFooter>
      </ModalInnerContainer>
    </Modal>
  );
}

export default ProjectScenesUpdate;
